import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Edit, Save, X, Download } from 'lucide-react';
import { apiClient } from '../../../../utils/apiClient'; // Adjust import path as needed

const EnvVar = ({projectName, gitHubRepo, branchUsed}) => {
  const { projectId, serviceId } = useParams();
  const [envContent, setEnvContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditButton, setShowEditButton] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const serviceName = `${gitHubRepo}-${branchUsed}`

  const fetchEnvFile = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.get(`/${projectName}/service/${serviceName}/env`, {
        projectId,
        resourceId: serviceId,
        resourceType: 'BACKEND_SERVICE',
        requiredPermission: 'view:service_env_vars'
      });
      
      if (data) {
        setEnvContent(data.content);
        setOriginalContent(data.content);
      }
    } catch (err) {
      console.error('Error fetching env file:', err);
      setError('Failed to fetch environment file. Please try again.');
      setEnvContent('');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, serviceId, projectName, serviceName]);

  const saveEnvFile = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const data = await apiClient.put(`/${projectName}/service/${serviceName}/env`, 
        { content: envContent },
        {
          projectId,
          resourceId: serviceId,
          resourceType: 'BACKEND_SERVICE',
          requiredPermission: 'update:service_env_vars'
        }
      );
      
      if (data) {
        setOriginalContent(envContent);
        setIsEditing(false);
      }
    } catch (err) {
      console.error('Error saving env file:', err);
      setError('Failed to save environment file. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const downloadEnvFile = async () => {
    try {
      const response = await apiClient.get(`/${projectName}/service/${serviceName}/env/download`, {
        projectId,
        resourceId: serviceId,
        resourceType: 'BACKEND_SERVICE',
        requiredPermission: 'download:service_env_vars'
      });
      
      if (!response) {
        throw new Error('Failed to download environment file');
      }
      
      // Assuming the backend returns a base64 encoded file or provides a download mechanism
      if (response.base64Content) {
        const blob = new Blob([atob(response.base64Content)], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = '.env';
        document.body.appendChild(a);
        a.click();
        
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Error downloading env file:', err);
      setError('Failed to download environment file.');
    }
  };

  useEffect(() => {
    fetchEnvFile();
  }, [fetchEnvFile]);

  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">Environment Variables</h2>
          <p className="text-gray-500 text-sm">
            Manage environment variables for your service
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {!isEditing ? (
            <button
              onClick={downloadEnvFile}
              className="inline-flex items-center text-sm px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-200 text-white rounded-lg transition-all duration-200 font-medium shadow-md disabled:opacity-70 disabled:cursor-not-allowed gap-2"
              title="Download Environment File"
            >
              <Download className="h-4 w-4" />
              Download Env
            </button>
          ): null}
        </div>
      </div>

      {error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div 
          className="relative bg-gray-50 rounded-md border border-gray-200"
          onMouseEnter={() => !isEditing && setShowEditButton(true)}
          onMouseLeave={() => !isEditing && setShowEditButton(false)}
        >
          {isEditing ? (
            <textarea
              value={envContent}
              onChange={(e) => setEnvContent(e.target.value)}
              className="w-full h-[32rem] font-mono text-sm p-4 bg-white rounded-md border-0 focus:ring-2 focus:ring-indigo-500"
              spellCheck="false"
            />
          ) : (
            <>
              <pre className="w-full h-[32rem] font-mono text-sm p-4 overflow-auto whitespace-pre">
                {envContent || 'No environment variables found. Click Edit to add some.'}
              </pre>
              {showEditButton && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2"
                >
                  <Edit className="h-5 w-5 text-gray-600" />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {isEditing && (
        <div className="flex justify-end pt-4 gap-2">
          <button
            onClick={() => {
              setIsEditing(false);
              setEnvContent(originalContent);
            }}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors mr-2"
          >
            Cancel
          </button>
          <button
            onClick={saveEnvFile}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
};

export default EnvVar;